/**
 * FlahaSOIL v2 — physics result card.
 *
 * Two-tier layout: highlighted plant-available water / field
 * capacity / wilting point block, then structure & conductivity.
 * Progressive FieldHelp keeps method detail behind expand.
 */
import {
	Box,
	Card,
	CardContent,
	CardHeader,
	Divider,
	Grid,
	Stack,
	Typography,
} from "@mui/material";
import type { SoilPhysicsResultDTO } from "@flaha/shared-types";

import { PHYSICS_HELP, PHYSICS_METHOD } from "../utils/agronomicCopy";
import { FieldHelp } from "./FieldHelp";

interface PhysicsResultCardProps {
	result: SoilPhysicsResultDTO | null | undefined;
}

export function PhysicsResultCard({ result }: PhysicsResultCardProps) {
	return (
		<Card variant="outlined" sx={{ bgcolor: "transparent", border: 0 }}>
			<CardHeader
				title="Soil physics"
				subheader="Water-holding and structure from texture & organic matter."
			/>
			<Divider sx={{ borderColor: "workspace.border" }} />
			<CardContent>
				{!result ? (
					<Typography variant="body2" color="text.secondary">
						No physics result yet.
					</Typography>
				) : (
					<Stack spacing={2.5}>
						<Grid container spacing={2}>
							<Grid item xs={12} sm={4}>
								<HighlightStat
									label="Plant-available water"
									value={fmtPct(result.plantAvailableWater)}
									summary={PHYSICS_HELP.plantAvailableWater}
									detail={PHYSICS_METHOD.plantAvailableWater}
									emphasised
								/>
							</Grid>
							<Grid item xs={6} sm={4}>
								<HighlightStat
									label="Field capacity"
									value={fmtPct(result.fieldCapacity)}
									summary={PHYSICS_HELP.fieldCapacity}
									detail={PHYSICS_METHOD.fieldCapacity}
								/>
							</Grid>
							<Grid item xs={6} sm={4}>
								<HighlightStat
									label="Wilting point"
									value={fmtPct(result.wiltingPoint)}
									summary={PHYSICS_HELP.wiltingPoint}
									detail={PHYSICS_METHOD.wiltingPoint}
								/>
							</Grid>
						</Grid>

						<Divider flexItem sx={{ borderColor: "workspace.border" }} />

						<Box>
							<Typography
								variant="overline"
								color="text.secondary"
								sx={{ display: "block", mb: 1 }}
							>
								Structure &amp; conductivity
							</Typography>
							<Stack spacing={1}>
								<DetailRow
									label="Texture class"
									value={result.textureClass}
									summary={PHYSICS_HELP.textureClass}
									detail={PHYSICS_METHOD.textureClass}
								/>
								<DetailRow
									label="Saturation"
									value={fmtPct(result.saturation)}
									summary={PHYSICS_HELP.saturation}
									detail={PHYSICS_METHOD.saturation}
								/>
								<DetailRow
									label="Saturated conductivity"
									value={`${fmt(result.saturatedConductivity)} mm/h`}
									summary={PHYSICS_HELP.saturatedConductivity}
									detail={PHYSICS_METHOD.saturatedConductivity}
								/>
								<DetailRow
									label="Bulk density"
									value={`${fmt(result.bulkDensity)} g/cm\u00B3`}
									summary={PHYSICS_HELP.bulkDensity}
									detail={PHYSICS_METHOD.bulkDensity}
								/>
								<DetailRow
									label="Drainage class"
									value={result.drainageClass ?? "\u2014"}
								/>
							</Stack>
						</Box>
					</Stack>
				)}
			</CardContent>
		</Card>
	);
}

function HighlightStat({
	label,
	value,
	summary,
	detail,
	emphasised = false,
}: {
	label: string;
	value: string;
	summary?: string | undefined;
	detail?: string | undefined;
	emphasised?: boolean | undefined;
}) {
	return (
		<Box>
			<Typography variant="caption" color="text.secondary">
				{label}
			</Typography>
			<Typography
				variant={emphasised ? "h4" : "h5"}
				fontWeight={600}
				color={emphasised ? "primary.main" : "text.primary"}
				sx={{ lineHeight: 1.1, my: 0.25 }}
			>
				{value}
			</Typography>
			<FieldHelp summary={summary} detail={detail} />
		</Box>
	);
}

function DetailRow({
	label,
	value,
	summary,
	detail,
}: {
	label: string;
	value: React.ReactNode;
	summary?: string | undefined;
	detail?: string | undefined;
}) {
	return (
		<Box>
			<Stack direction="row" justifyContent="space-between" alignItems="center">
				<Typography variant="body2" color="text.secondary">
					{label}
				</Typography>
				<Typography variant="body2" fontWeight={500}>
					{value}
				</Typography>
			</Stack>
			<FieldHelp summary={summary} detail={detail} />
		</Box>
	);
}

function fmt(n: number | null | undefined): string {
	if (n === null || n === undefined) return "\u2014";
	return Number.isInteger(n) ? String(n) : n.toFixed(3);
}

function fmtPct(n: number | null | undefined): string {
	if (n === null || n === undefined) return "\u2014";
	return `${n.toFixed(1)} %`;
}
